import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Owner activation feature state.
 *
 * Two independent systems share one owner-bound, versioned aggregate:
 * - `wizard`: initial education wizard shown once to newly registered owners;
 * - `tracker`: location-scoped post-wizard adoption milestones that replace
 *   Quick Actions on Home until every task is complete and acknowledged.
 *
 * Persisted through the root redux-persist store. See
 * specs/001-new-owner-setup-wizard/data-model.md for the full contract.
 */

export const OWNER_ACTIVATION_SCHEMA_VERSION = 2 as const;
export const OWNER_SETUP_FEATURE_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OwnerSetupSessionBootstrap =
  | 'not_required'
  | 'pending'
  | 'requires_login'
  | 'ready';

export type OwnerSetupSessionBootstrapError =
  | 'network'
  | 'invalid_session'
  | 'employee_profile_unavailable'
  | null;

export type OwnerSetupEligibility = {
  eligible: boolean;
  source: 'business_setup_completion' | null;
  markedAt: string | null;
  featureVersion: typeof OWNER_SETUP_FEATURE_VERSION;
  sessionBootstrap: OwnerSetupSessionBootstrap;
  sessionBootstrapError: OwnerSetupSessionBootstrapError;
};

export type SetupWizardStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'skipped';

export type SetupWizardEntryMode = 'initial' | 'resume' | 'replay';

export type SetupWizardStepId =
  | 'setup_progress'
  | 'workspace'
  | 'locations'
  | 'sale_loop'
  | 'first_product'
  | 'preferences'
  | 'ready_for_home';

export const SETUP_WIZARD_STEPS: readonly SetupWizardStepId[] = [
  'setup_progress',
  'workspace',
  'sale_loop',
  'first_product',
  'preferences',
  'locations',
  'ready_for_home',
];

export type SetupWizardProgress = {
  status: SetupWizardStatus;
  currentStepId: SetupWizardStepId | null;
  highestCompletedStepIndex: number;
  entryMode: SetupWizardEntryMode;
  startedAt: string | null;
  lastViewedAt: string | null;
  completedAt: string | null;
  skippedAt: string | null;
  skippedFromStepId: SetupWizardStepId | null;
  productCreatedDuringWizard: boolean;
  /** Owner opted-in to the test location during the wizard location step. */
  testLocationOptedIn: boolean;
  /**
   * True once the wizard exits (completed OR skipped) and until the Home
   * landing celebration modal is shown for the first time. Owner-global; not
   * per-location. Cleared as soon as the modal mounts so it never replays.
   */
  homeLandingCelebrationPending: boolean;
};

/** All activation task IDs (location-scoped + global). */
export type ActivationTaskId =
  | 'add_product'
  | 'first_sale'
  | 'explore_backoffice'
  | 'add_staff'
  | 'visit_test_location';

/** Location-scoped tasks (everything except visit_test_location). */
export type LocationScopedActivationTaskId =
  | 'add_product'
  | 'first_sale'
  | 'explore_backoffice'
  | 'add_staff';

export const LOCATION_SCOPED_TASK_IDS: readonly LocationScopedActivationTaskId[] =
  ['add_product', 'first_sale', 'explore_backoffice', 'add_staff'];

export type ActivationCompletionSource =
  | 'wizard_product_create'
  | 'feature_success'
  | 'bootstrap_existing_data';

export type ActivationLocationType = 'real' | 'test';

export type ActivationTaskState = {
  completedAt: string | null;
  completionSource: ActivationCompletionSource | null;
  locationId: string | null;
  locationType: ActivationLocationType | null;
};

export type ActivationTaskDefinition = {
  id: ActivationTaskId;
  order: number;
  dependsOn: ActivationTaskId[];
};

export const ACTIVATION_TASKS: readonly ActivationTaskDefinition[] = [
  { id: 'add_product', order: 1, dependsOn: [] },
  { id: 'first_sale', order: 2, dependsOn: ['add_product'] },
  { id: 'explore_backoffice', order: 3, dependsOn: [] },
  { id: 'add_staff', order: 4, dependsOn: [] },
  { id: 'visit_test_location', order: 5, dependsOn: [] },
];

export const ACTIVATION_TASK_IDS: readonly ActivationTaskId[] =
  ACTIVATION_TASKS.map(task => task.id);

/**
 * Task IDs required for completion.
 *
 * - `add_staff` is intentionally excluded product-wide: adding employees was
 *   friction for solo owners who never invite anyone, so it no longer gates
 *   completion. Existing persisted `add_staff` state stays in the store
 *   harmlessly (dead weight) — nothing renders it or counts it.
 * - `visit_test_location` is excluded when the owner didn't opt in during the
 *   wizard.
 */
export const getRequiredTaskIds = (
  testLocationOptedIn: boolean,
): readonly ActivationTaskId[] => {
  const excluded = new Set<ActivationTaskId>(['add_staff']);
  if (!testLocationOptedIn) excluded.add('visit_test_location');
  return ACTIVATION_TASK_IDS.filter(id => !excluded.has(id));
};

export type ActivationConfettiOutcome =
  | 'played'
  | 'reduced_motion'
  | 'render_failed';

/** Per-location activation progress bucket. */
export type LocationActivationProgress = {
  tasks: Record<LocationScopedActivationTaskId, ActivationTaskState>;
  collapsed: boolean;
  allCompletedAt: string | null;
  celebrationHandledAt: string | null;
  confettiOutcome: ActivationConfettiOutcome | null;
  completionAcknowledgedAt: string | null;
  lastHighlightedTaskId: ActivationTaskId | null;
};

/** v2 location-scoped tracker. */
export type ActivationTrackerProgress = {
  /** Per real location. Test locations MUST NOT get an entry. */
  byLocation: Record<string /* locationId */, LocationActivationProgress>;
  /** Owner-global: complete once, visible on every real location checklist. */
  visitTestLocation: ActivationTaskState;
};

export type ActivationTrackerPhase =
  | 'inactive'
  | 'active'
  | 'completed_unacknowledged'
  | 'completed_acknowledged';

export type SimplifiedProductDraft = {
  productName: string;
  category: string;
  buyingPrice: string;
  sellingPrice: string;
  quantity: string;
  updatedAt: string | null;
};

export type OwnerActivationState = {
  schemaVersion: typeof OWNER_ACTIVATION_SCHEMA_VERSION;
  ownerKey: string | null;
  eligibility: OwnerSetupEligibility;
  wizard: SetupWizardProgress;
  tracker: ActivationTrackerProgress;
  productDraft: SimplifiedProductDraft;
};

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialEligibility = (): OwnerSetupEligibility => ({
  eligible: false,
  source: null,
  markedAt: null,
  featureVersion: OWNER_SETUP_FEATURE_VERSION,
  sessionBootstrap: 'not_required',
  sessionBootstrapError: null,
});

const initialWizard = (): SetupWizardProgress => ({
  status: 'not_started',
  currentStepId: null,
  highestCompletedStepIndex: -1,
  entryMode: 'initial',
  startedAt: null,
  lastViewedAt: null,
  completedAt: null,
  skippedAt: null,
  skippedFromStepId: null,
  productCreatedDuringWizard: false,
  testLocationOptedIn: false,
  homeLandingCelebrationPending: false,
});

export const initialTaskState = (): ActivationTaskState => ({
  completedAt: null,
  completionSource: null,
  locationId: null,
  locationType: null,
});

const initialLocationTasks = (): Record<
  LocationScopedActivationTaskId,
  ActivationTaskState
> => ({
  add_product: initialTaskState(),
  first_sale: initialTaskState(),
  explore_backoffice: initialTaskState(),
  add_staff: initialTaskState(),
});

/** Create an empty location progress bucket. */
export const createInitialLocationProgress = (): LocationActivationProgress => ({
  tasks: initialLocationTasks(),
  collapsed: false,
  allCompletedAt: null,
  celebrationHandledAt: null,
  confettiOutcome: null,
  completionAcknowledgedAt: null,
  lastHighlightedTaskId: null,
});

const initialTracker = (): ActivationTrackerProgress => ({
  byLocation: {},
  visitTestLocation: initialTaskState(),
});

const initialProductDraft = (): SimplifiedProductDraft => ({
  productName: '',
  category: '',
  buyingPrice: '',
  sellingPrice: '',
  quantity: '',
  updatedAt: null,
});

export const createInitialOwnerActivationState = (): OwnerActivationState => ({
  schemaVersion: OWNER_ACTIVATION_SCHEMA_VERSION,
  ownerKey: null,
  eligibility: initialEligibility(),
  wizard: initialWizard(),
  tracker: initialTracker(),
  productDraft: initialProductDraft(),
});

const initialState: OwnerActivationState = createInitialOwnerActivationState();

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const stepIndex = (stepId: SetupWizardStepId): number =>
  SETUP_WIZARD_STEPS.indexOf(stepId);

const isKnownStepId = (value: unknown): value is SetupWizardStepId =>
  typeof value === 'string' &&
  SETUP_WIZARD_STEPS.includes(value as SetupWizardStepId);

const isKnownTaskId = (value: unknown): value is ActivationTaskId =>
  typeof value === 'string' &&
  ACTIVATION_TASK_IDS.includes(value as ActivationTaskId);

/** Check if a task ID is location-scoped (not visit_test_location). */
export const isLocationScopedTask = (
  taskId: ActivationTaskId,
): taskId is LocationScopedActivationTaskId =>
  LOCATION_SCOPED_TASK_IDS.includes(taskId as LocationScopedActivationTaskId);

/**
 * Get merged tasks for a location: 4 location-scoped + global visit.
 * Returns empty progress without inserting a bucket for missing locations.
 */
export const getMergedActivationTasks = (
  tracker: ActivationTrackerProgress,
  locationId: string | null,
): Record<ActivationTaskId, ActivationTaskState> => {
  const locationProgress = locationId ? tracker.byLocation[locationId] : null;
  const tasks = locationProgress?.tasks ?? initialLocationTasks();
  return {
    ...tasks,
    visit_test_location: tracker.visitTestLocation,
  };
};

/** Check if all required tasks are complete for a location. */
const allTasksCompleteForLocation = (
  tracker: ActivationTrackerProgress,
  locationId: string,
  testLocationOptedIn: boolean,
): boolean => {
  const merged = getMergedActivationTasks(tracker, locationId);
  return getRequiredTaskIds(testLocationOptedIn).every(
    taskId => merged[taskId].completedAt !== null,
  );
};

/** Recompute and update allCompletedAt for a specific location bucket. */
const updateLocationAllCompletedAt = (
  tracker: ActivationTrackerProgress,
  locationId: string,
  timestamp: string,
  testLocationOptedIn: boolean,
): void => {
  const bucket = tracker.byLocation[locationId];
  if (!bucket) return;
  if (bucket.allCompletedAt !== null) return;
  if (allTasksCompleteForLocation(tracker, locationId, testLocationOptedIn)) {
    bucket.allCompletedAt = timestamp;
  }
};

const SOURCE_PRECEDENCE: Record<ActivationCompletionSource, number> = {
  wizard_product_create: 3,
  feature_success: 2,
  bootstrap_existing_data: 1,
};

const isIsoString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

// ---------------------------------------------------------------------------
// Pure helpers for phase derivation (exported for tests)
// ---------------------------------------------------------------------------

type PhaseInput = {
  wizard: SetupWizardProgress;
  tracker: ActivationTrackerProgress;
  locationId: string | null;
  isTest: boolean;
  isSwitching: boolean;
  /**
   * Whether the current location is the owner's default (first/primary)
   * location. The Getting Started tracker is anchored to that location
   * only — locations the owner adds later are treated as "user already
   * knows the ropes" and skip the tracker.
   */
  isDefault: boolean;
};

/**
 * Derive activation tracker phase for a given location context.
 * Pure helper for tests and selectors.
 */
export const getLocationActivationPhase = ({
  wizard,
  tracker,
  locationId,
  isTest,
  isSwitching,
  isDefault,
}: PhaseInput): ActivationTrackerPhase => {
  if (wizard.status !== 'completed' && wizard.status !== 'skipped') {
    return 'inactive';
  }
  if (!locationId || isTest || isSwitching || !isDefault) {
    return 'inactive';
  }
  const merged = getMergedActivationTasks(tracker, locationId);
  const requiredIds = getRequiredTaskIds(wizard.testLocationOptedIn);
  const allComplete = requiredIds.every(
    taskId => merged[taskId].completedAt !== null,
  );
  if (!allComplete) {
    return 'active';
  }
  const bucket = tracker.byLocation[locationId];
  if (!bucket || bucket.completionAcknowledgedAt === null) {
    return 'completed_unacknowledged';
  }
  return 'completed_acknowledged';
};

// ---------------------------------------------------------------------------
// Migration / normalization
// ---------------------------------------------------------------------------

const VALID_BOOTSTRAP: readonly OwnerSetupSessionBootstrap[] = [
  'not_required',
  'pending',
  'requires_login',
  'ready',
];

const VALID_BOOTSTRAP_ERROR = [
  'network',
  'invalid_session',
  'employee_profile_unavailable',
] as const;

const VALID_WIZARD_STATUS: readonly SetupWizardStatus[] = [
  'not_started',
  'in_progress',
  'completed',
  'skipped',
];

const VALID_ENTRY_MODE: readonly SetupWizardEntryMode[] = [
  'initial',
  'resume',
  'replay',
];

const VALID_COMPLETION_SOURCE: readonly ActivationCompletionSource[] = [
  'wizard_product_create',
  'feature_success',
  'bootstrap_existing_data',
];

const VALID_CONFETTI_OUTCOME: readonly ActivationConfettiOutcome[] = [
  'played',
  'reduced_motion',
  'render_failed',
];

/** Normalize a single task state from persisted data. */
const normalizeTaskState = (rawTask: unknown): ActivationTaskState => {
  const empty = initialTaskState();
  if (!rawTask || typeof rawTask !== 'object') return empty;
  const task = rawTask as Record<string, unknown>;
  if (!isIsoString(task.completedAt)) return empty;
  return {
    completedAt: task.completedAt,
    completionSource: VALID_COMPLETION_SOURCE.includes(
      task.completionSource as ActivationCompletionSource,
    )
      ? (task.completionSource as ActivationCompletionSource)
      : 'feature_success',
    locationId: isIsoString(task.locationId) ? task.locationId : null,
    locationType:
      task.locationType === 'real' || task.locationType === 'test'
        ? task.locationType
        : null,
  };
};

/** Normalize v2 tracker shape. */
const normalizeV2Tracker = (rawTracker: unknown): ActivationTrackerProgress => {
  const fresh = initialTracker();
  if (!rawTracker || typeof rawTracker !== 'object') return fresh;
  const tracker = rawTracker as Record<string, unknown>;

  fresh.visitTestLocation = normalizeTaskState(tracker.visitTestLocation);

  if (tracker.byLocation && typeof tracker.byLocation === 'object') {
    const byLoc = tracker.byLocation as Record<string, unknown>;
    for (const locId of Object.keys(byLoc)) {
      if (!locId || typeof locId !== 'string') continue;
      const rawBucket = byLoc[locId];
      if (!rawBucket || typeof rawBucket !== 'object') continue;
      const bucket = rawBucket as Record<string, unknown>;

      const tasks = initialLocationTasks();
      if (bucket.tasks && typeof bucket.tasks === 'object') {
        const rawTasks = bucket.tasks as Record<string, unknown>;
        for (const taskId of LOCATION_SCOPED_TASK_IDS) {
          const normalized = normalizeTaskState(rawTasks[taskId]);
          if (normalized.completedAt !== null) {
            tasks[taskId] = normalized;
          }
        }
      }

      // Must match runtime completion gates in getRequiredTaskIds:
      // - visit_test_location is owner-global / opt-in (not in this bucket)
      // - add_staff is stored but never required product-wide
      // Gating ack/celebration on LOCATION_SCOPED_TASK_IDS (which still
      // includes add_staff) wiped completionAcknowledgedAt on every
      // rehydrate for owners who finished the 3 required tasks only.
      const requiredLocationScopedIds = LOCATION_SCOPED_TASK_IDS.filter(
        id => id !== 'add_staff',
      );
      const allComplete = requiredLocationScopedIds.every(
        tid => tasks[tid].completedAt !== null,
      );

      fresh.byLocation[locId] = {
        tasks,
        collapsed: bucket.collapsed === true,
        allCompletedAt:
          allComplete && isIsoString(bucket.allCompletedAt)
            ? bucket.allCompletedAt
            : null,
        celebrationHandledAt:
          allComplete && isIsoString(bucket.celebrationHandledAt)
            ? bucket.celebrationHandledAt
            : null,
        confettiOutcome:
          allComplete &&
          VALID_CONFETTI_OUTCOME.includes(
            bucket.confettiOutcome as ActivationConfettiOutcome,
          )
            ? (bucket.confettiOutcome as ActivationConfettiOutcome)
            : null,
        completionAcknowledgedAt:
          allComplete && isIsoString(bucket.completionAcknowledgedAt)
            ? bucket.completionAcknowledgedAt
            : null,
        lastHighlightedTaskId: isKnownTaskId(bucket.lastHighlightedTaskId)
          ? bucket.lastHighlightedTaskId
          : null,
      };
    }
  }

  return fresh;
};

/** Migrate v1 to v2: preserve wizard/eligibility, reset tracker. */
const migrateV1ToV2 = (raw: Record<string, unknown>): OwnerActivationState => {
  const fresh = createInitialOwnerActivationState();
  const ownerKey = isIsoString(raw.ownerKey) ? raw.ownerKey : null;

  // Eligibility
  const rawEligibility =
    raw.eligibility && typeof raw.eligibility === 'object'
      ? (raw.eligibility as Record<string, unknown>)
      : {};
  const eligible =
    rawEligibility.eligible === true &&
    ownerKey !== null &&
    rawEligibility.source === 'business_setup_completion' &&
    rawEligibility.featureVersion === OWNER_SETUP_FEATURE_VERSION;

  const eligibility: OwnerSetupEligibility = eligible
    ? {
        eligible: true,
        source: 'business_setup_completion',
        markedAt: isIsoString(rawEligibility.markedAt)
          ? rawEligibility.markedAt
          : null,
        featureVersion: OWNER_SETUP_FEATURE_VERSION,
        sessionBootstrap: VALID_BOOTSTRAP.includes(
          rawEligibility.sessionBootstrap as OwnerSetupSessionBootstrap,
        )
          ? (rawEligibility.sessionBootstrap as OwnerSetupSessionBootstrap)
          : 'not_required',
        sessionBootstrapError: VALID_BOOTSTRAP_ERROR.includes(
          rawEligibility.sessionBootstrapError as (typeof VALID_BOOTSTRAP_ERROR)[number],
        )
          ? (rawEligibility.sessionBootstrapError as OwnerSetupSessionBootstrapError)
          : null,
      }
    : initialEligibility();

  // Wizard
  const rawWizard =
    raw.wizard && typeof raw.wizard === 'object'
      ? (raw.wizard as Record<string, unknown>)
      : {};
  const status: SetupWizardStatus = VALID_WIZARD_STATUS.includes(
    rawWizard.status as SetupWizardStatus,
  )
    ? (rawWizard.status as SetupWizardStatus)
    : 'not_started';
  const wizard: SetupWizardProgress = {
    status,
    currentStepId: isKnownStepId(rawWizard.currentStepId)
      ? rawWizard.currentStepId
      : status === 'in_progress'
        ? 'setup_progress'
        : null,
    highestCompletedStepIndex:
      typeof rawWizard.highestCompletedStepIndex === 'number' &&
      Number.isInteger(rawWizard.highestCompletedStepIndex) &&
      rawWizard.highestCompletedStepIndex >= -1 &&
      rawWizard.highestCompletedStepIndex < SETUP_WIZARD_STEPS.length
        ? rawWizard.highestCompletedStepIndex
        : -1,
    entryMode: VALID_ENTRY_MODE.includes(
      rawWizard.entryMode as SetupWizardEntryMode,
    )
      ? (rawWizard.entryMode as SetupWizardEntryMode)
      : 'initial',
    startedAt: isIsoString(rawWizard.startedAt) ? rawWizard.startedAt : null,
    lastViewedAt: isIsoString(rawWizard.lastViewedAt)
      ? rawWizard.lastViewedAt
      : null,
    completedAt:
      status === 'completed' && isIsoString(rawWizard.completedAt)
        ? rawWizard.completedAt
        : null,
    skippedAt:
      status === 'skipped' && isIsoString(rawWizard.skippedAt)
        ? rawWizard.skippedAt
        : null,
    skippedFromStepId:
      status === 'skipped' && isKnownStepId(rawWizard.skippedFromStepId)
        ? rawWizard.skippedFromStepId
        : null,
    productCreatedDuringWizard: rawWizard.productCreatedDuringWizard === true,
    testLocationOptedIn: rawWizard.testLocationOptedIn === true,
    homeLandingCelebrationPending:
      rawWizard.homeLandingCelebrationPending === true,
  };

  // Product draft
  const rawDraft =
    raw.productDraft && typeof raw.productDraft === 'object'
      ? (raw.productDraft as Record<string, unknown>)
      : {};
  const draftText = (value: unknown): string =>
    typeof value === 'string' ? value : '';
  const productDraft: SimplifiedProductDraft = {
    productName: draftText(rawDraft.productName),
    category: draftText(rawDraft.category),
    buyingPrice: draftText(rawDraft.buyingPrice),
    sellingPrice: draftText(rawDraft.sellingPrice),
    quantity: draftText(rawDraft.quantity),
    updatedAt: isIsoString(rawDraft.updatedAt) ? rawDraft.updatedAt : null,
  };

  return {
    schemaVersion: OWNER_ACTIVATION_SCHEMA_VERSION,
    ownerKey,
    eligibility,
    wizard,
    tracker: initialTracker(), // v1→v2: reset tracker with empty byLocation
    productDraft,
  };
};

/**
 * Normalize any persisted value into a safe OwnerActivationState.
 *
 * Migration branches:
 * - schemaVersion === 2: normalize v2 shape
 * - schemaVersion === 1: preserve wizard/eligibility/productDraft, reset tracker to v2 empty
 * - missing/unknown/future: full fresh reset
 */
export function migrateOwnerActivationState(
  persisted: unknown,
): OwnerActivationState {
  const fresh = createInitialOwnerActivationState();

  if (
    persisted === null ||
    persisted === undefined ||
    typeof persisted !== 'object' ||
    Array.isArray(persisted)
  ) {
    return fresh;
  }

  const raw = persisted as Record<string, unknown>;

  // v2: normalize
  if (raw.schemaVersion === 2) {
    const ownerKey = isIsoString(raw.ownerKey) ? raw.ownerKey : null;

    // Eligibility
    const rawEligibility =
      raw.eligibility && typeof raw.eligibility === 'object'
        ? (raw.eligibility as Record<string, unknown>)
        : {};
    const eligible =
      rawEligibility.eligible === true &&
      ownerKey !== null &&
      rawEligibility.source === 'business_setup_completion' &&
      rawEligibility.featureVersion === OWNER_SETUP_FEATURE_VERSION;

    const eligibility: OwnerSetupEligibility = eligible
      ? {
          eligible: true,
          source: 'business_setup_completion',
          markedAt: isIsoString(rawEligibility.markedAt)
            ? rawEligibility.markedAt
            : null,
          featureVersion: OWNER_SETUP_FEATURE_VERSION,
          sessionBootstrap: VALID_BOOTSTRAP.includes(
            rawEligibility.sessionBootstrap as OwnerSetupSessionBootstrap,
          )
            ? (rawEligibility.sessionBootstrap as OwnerSetupSessionBootstrap)
            : 'not_required',
          sessionBootstrapError: VALID_BOOTSTRAP_ERROR.includes(
            rawEligibility.sessionBootstrapError as (typeof VALID_BOOTSTRAP_ERROR)[number],
          )
            ? (rawEligibility.sessionBootstrapError as OwnerSetupSessionBootstrapError)
            : null,
        }
      : initialEligibility();

    // Wizard
    const rawWizard =
      raw.wizard && typeof raw.wizard === 'object'
        ? (raw.wizard as Record<string, unknown>)
        : {};
    const status: SetupWizardStatus = VALID_WIZARD_STATUS.includes(
      rawWizard.status as SetupWizardStatus,
    )
      ? (rawWizard.status as SetupWizardStatus)
      : 'not_started';
    const wizard: SetupWizardProgress = {
      status,
      currentStepId: isKnownStepId(rawWizard.currentStepId)
        ? rawWizard.currentStepId
        : status === 'in_progress'
          ? 'setup_progress'
          : null,
      highestCompletedStepIndex:
        typeof rawWizard.highestCompletedStepIndex === 'number' &&
        Number.isInteger(rawWizard.highestCompletedStepIndex) &&
        rawWizard.highestCompletedStepIndex >= -1 &&
        rawWizard.highestCompletedStepIndex < SETUP_WIZARD_STEPS.length
          ? rawWizard.highestCompletedStepIndex
          : -1,
      entryMode: VALID_ENTRY_MODE.includes(
        rawWizard.entryMode as SetupWizardEntryMode,
      )
        ? (rawWizard.entryMode as SetupWizardEntryMode)
        : 'initial',
      startedAt: isIsoString(rawWizard.startedAt) ? rawWizard.startedAt : null,
      lastViewedAt: isIsoString(rawWizard.lastViewedAt)
        ? rawWizard.lastViewedAt
        : null,
      completedAt:
        status === 'completed' && isIsoString(rawWizard.completedAt)
          ? rawWizard.completedAt
          : null,
      skippedAt:
        status === 'skipped' && isIsoString(rawWizard.skippedAt)
          ? rawWizard.skippedAt
          : null,
      skippedFromStepId:
        status === 'skipped' && isKnownStepId(rawWizard.skippedFromStepId)
          ? rawWizard.skippedFromStepId
          : null,
      productCreatedDuringWizard: rawWizard.productCreatedDuringWizard === true,
      testLocationOptedIn: rawWizard.testLocationOptedIn === true,
      homeLandingCelebrationPending:
        rawWizard.homeLandingCelebrationPending === true,
    };

    // Tracker (v2)
    const tracker = normalizeV2Tracker(raw.tracker);

    // Product draft
    const rawDraft =
      raw.productDraft && typeof raw.productDraft === 'object'
        ? (raw.productDraft as Record<string, unknown>)
        : {};
    const draftText = (value: unknown): string =>
      typeof value === 'string' ? value : '';
    const productDraft: SimplifiedProductDraft = {
      productName: draftText(rawDraft.productName),
      category: draftText(rawDraft.category),
      buyingPrice: draftText(rawDraft.buyingPrice),
      sellingPrice: draftText(rawDraft.sellingPrice),
      quantity: draftText(rawDraft.quantity),
      updatedAt: isIsoString(rawDraft.updatedAt) ? rawDraft.updatedAt : null,
    };

    return {
      schemaVersion: OWNER_ACTIVATION_SCHEMA_VERSION,
      ownerKey,
      eligibility,
      wizard,
      tracker,
      productDraft,
    };
  }

  // v1: preserve wizard/eligibility/productDraft, reset tracker
  if (raw.schemaVersion === 1) {
    return migrateV1ToV2(raw);
  }

  // Unknown/future schema: full reset
  return fresh;
}

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const ownerActivationSlice = createSlice({
  name: 'ownerActivation',
  initialState,
  reducers: {
    /**
     * Mark a freshly converted new owner eligible for the initial wizard.
     * Only the business-setup success path may dispatch this.
     */
    markOwnerSetupEligible: (
      state,
      action: PayloadAction<{
        ownerKey: string;
        markedAt: string;
        source: 'business_setup_completion';
        featureVersion: typeof OWNER_SETUP_FEATURE_VERSION;
        sessionBootstrap: 'pending' | 'ready';
      }>,
    ) => {
      const { ownerKey, markedAt, source, featureVersion, sessionBootstrap } =
        action.payload;
      if (
        !ownerKey ||
        source !== 'business_setup_completion' ||
        featureVersion !== OWNER_SETUP_FEATURE_VERSION
      ) {
        return;
      }
      if (state.ownerKey !== null && state.ownerKey !== ownerKey) {
        Object.assign(state, createInitialOwnerActivationState());
      }
      state.ownerKey = ownerKey;
      state.eligibility.eligible = true;
      state.eligibility.source = source;
      state.eligibility.markedAt = markedAt;
      state.eligibility.featureVersion = featureVersion;
      state.eligibility.sessionBootstrap = sessionBootstrap;
      state.eligibility.sessionBootstrapError = null;
    },

    /** The interrupted session recovery is complete: profile/preload success. */
    markOwnerSetupSessionReady: state => {
      if (!state.eligibility.eligible) return;
      state.eligibility.sessionBootstrap = 'ready';
      state.eligibility.sessionBootstrapError = null;
    },

    /** Record a recoverable session bootstrap failure without losing eligibility. */
    markOwnerSetupSessionBootstrapError: (
      state,
      action: PayloadAction<{
        error: Exclude<OwnerSetupSessionBootstrapError, null>;
      }>,
    ) => {
      if (!state.eligibility.eligible) return;
      state.eligibility.sessionBootstrapError = action.payload.error;
      if (action.payload.error === 'invalid_session') {
        state.eligibility.sessionBootstrap = 'requires_login';
      }
    },

    /**
     * Compare persisted owner binding with the authenticated identity.
     * Mismatch (or a null incoming key over bound state) resets the aggregate
     * so one owner's progress can never appear for another owner.
     */
    resetOwnerActivationForIdentity: (
      state,
      action: PayloadAction<{ ownerKey: string | null }>,
    ) => {
      const incoming = action.payload.ownerKey;
      if (state.ownerKey === null && incoming === null) return;
      if (state.ownerKey !== null && state.ownerKey === incoming) return;
      Object.assign(state, createInitialOwnerActivationState());
    },

    /** Reset the aggregate unconditionally (new registration start). */
    resetOwnerActivationState: state => {
      Object.assign(state, createInitialOwnerActivationState());
    },

    startOwnerSetupWizard: (
      state,
      action: PayloadAction<{
        startedAt: string;
        entryMode: 'initial' | 'resume';
      }>,
    ) => {
      if (!state.eligibility.eligible) return;
      if (
        state.wizard.status === 'completed' ||
        state.wizard.status === 'skipped'
      ) {
        return;
      }
      if (state.wizard.status === 'not_started') {
        state.wizard.status = 'in_progress';
        state.wizard.currentStepId = 'setup_progress';
        state.wizard.startedAt = action.payload.startedAt;
      }
      state.wizard.entryMode = action.payload.entryMode;
      state.wizard.lastViewedAt = action.payload.startedAt;
    },

    viewOwnerSetupStep: (
      state,
      action: PayloadAction<{ stepId: SetupWizardStepId; viewedAt: string }>,
    ) => {
      const { stepId, viewedAt } = action.payload;
      if (!isKnownStepId(stepId)) return;
      state.wizard.lastViewedAt = viewedAt;
      if (state.wizard.status === 'in_progress') {
        state.wizard.currentStepId = stepId;
      }
    },

    completeOwnerSetupStep: (
      state,
      action: PayloadAction<{ stepId: SetupWizardStepId }>,
    ) => {
      const { stepId } = action.payload;
      if (state.wizard.status !== 'in_progress') return;
      if (!isKnownStepId(stepId)) return;
      const index = stepIndex(stepId);
      if (index > state.wizard.highestCompletedStepIndex) {
        state.wizard.highestCompletedStepIndex = index;
      }
      const next = SETUP_WIZARD_STEPS[index + 1];
      if (next) {
        state.wizard.currentStepId = next;
      }
    },

    goBackOwnerSetupStep: state => {
      if (state.wizard.status !== 'in_progress') return;
      const current = state.wizard.currentStepId;
      if (!current) return;
      const index = stepIndex(current);
      if (index <= 0) return;
      state.wizard.currentStepId = SETUP_WIZARD_STEPS[index - 1];
    },

    skipOwnerSetupWizard: (
      state,
      action: PayloadAction<{
        stepId: Exclude<SetupWizardStepId, 'setup_progress'>;
        skippedAt: string;
      }>,
    ) => {
      const { stepId, skippedAt } = action.payload;
      if (state.wizard.status !== 'in_progress') return;
      if ((stepId as SetupWizardStepId) === 'setup_progress') return;
      if (!isKnownStepId(stepId)) return;
      state.wizard.status = 'skipped';
      state.wizard.skippedAt = skippedAt;
      state.wizard.skippedFromStepId = stepId;
      state.wizard.currentStepId = null;
      state.wizard.homeLandingCelebrationPending = true;
      state.productDraft = initialProductDraft();
    },

    completeOwnerSetupWizard: (
      state,
      action: PayloadAction<{ completedAt: string }>,
    ) => {
      if (state.wizard.status !== 'in_progress') return;
      state.wizard.status = 'completed';
      state.wizard.completedAt = action.payload.completedAt;
      state.wizard.highestCompletedStepIndex = SETUP_WIZARD_STEPS.length - 1;
      state.wizard.currentStepId = null;
      state.wizard.homeLandingCelebrationPending = true;
      state.productDraft = initialProductDraft();
    },

    /**
     * Mark the Home-landing celebration modal as shown so it never plays
     * again for this owner. Idempotent — safe to dispatch multiple times.
     */
    markWizardHomeLandingCelebrationShown: state => {
      state.wizard.homeLandingCelebrationPending = false;
    },

    enterOwnerSetupReplay: state => {
      if (
        state.wizard.status !== 'completed' &&
        state.wizard.status !== 'skipped'
      ) {
        return;
      }
      state.wizard.entryMode = 'replay';
    },

    exitOwnerSetupReplay: state => {
      if (state.wizard.entryMode !== 'replay') return;
      state.wizard.entryMode = 'initial';
    },

    updateSimplifiedProductDraft: (
      state,
      action: PayloadAction<
        Partial<Omit<SimplifiedProductDraft, 'updatedAt'>> & {
          updatedAt: string;
        }
      >,
    ) => {
      const { updatedAt, ...values } = action.payload;
      const assignable: Array<keyof Omit<SimplifiedProductDraft, 'updatedAt'>> =
        ['productName', 'category', 'buyingPrice', 'sellingPrice', 'quantity'];
      for (const key of assignable) {
        const value = values[key];
        if (typeof value === 'string') {
          state.productDraft[key] = value;
        }
      }
      state.productDraft.updatedAt = updatedAt;
    },

    clearSimplifiedProductDraft: state => {
      state.productDraft = initialProductDraft();
    },

    markWizardProductCreated: state => {
      state.wizard.productCreatedDuringWizard = true;
    },

    setTestLocationOptedIn: (
      state,
      action: PayloadAction<{ optedIn: boolean }>,
    ) => {
      state.wizard.testLocationOptedIn = action.payload.optedIn;
    },

    /**
     * Complete an activation task.
     * Location-scoped tasks require non-empty locationId + locationType === 'real'.
     * visit_test_location writes to global visitTestLocation only.
     */
    completeActivationTask: (
      state,
      action: PayloadAction<{
        taskId: ActivationTaskId;
        completedAt: string;
        completionSource: ActivationCompletionSource;
        locationId?: string | null;
        locationType?: ActivationLocationType | null;
      }>,
    ) => {
      const { taskId, completedAt, completionSource } = action.payload;
      const locationId = action.payload.locationId ?? null;
      const locationType = action.payload.locationType ?? null;

      if (!isKnownTaskId(taskId)) return;
      if (!VALID_COMPLETION_SOURCE.includes(completionSource)) return;

      // Handle visit_test_location (global)
      if (taskId === 'visit_test_location') {
        if (locationType !== 'test') return;

        const task = state.tracker.visitTestLocation;
        if (task.completedAt !== null) {
          const existingRank = task.completionSource
            ? SOURCE_PRECEDENCE[task.completionSource]
            : 0;
          if (SOURCE_PRECEDENCE[completionSource] > existingRank) {
            task.completionSource = completionSource;
          }
          if (task.locationId === null && locationId !== null) {
            task.locationId = locationId;
          }
          if (task.locationType === null && locationType !== null) {
            task.locationType = locationType;
          }
          return;
        }

        task.completedAt = completedAt;
        task.completionSource = completionSource;
        task.locationId = locationId;
        task.locationType = locationType;

        // When visit completes, update allCompletedAt for every real bucket
        for (const locId of Object.keys(state.tracker.byLocation)) {
          updateLocationAllCompletedAt(state.tracker, locId, completedAt, state.wizard.testLocationOptedIn);
        }
        return;
      }

      // Location-scoped tasks require real location
      if (!locationId || locationType !== 'real') return;

      // Lazy-init location bucket
      if (!state.tracker.byLocation[locationId]) {
        state.tracker.byLocation[locationId] = createInitialLocationProgress();
      }

      const bucket = state.tracker.byLocation[locationId];
      const task = bucket.tasks[taskId as LocationScopedActivationTaskId];

      if (task.completedAt !== null) {
        const existingRank = task.completionSource
          ? SOURCE_PRECEDENCE[task.completionSource]
          : 0;
        if (SOURCE_PRECEDENCE[completionSource] > existingRank) {
          task.completionSource = completionSource;
        }
        if (task.locationId === null && locationId !== null) {
          task.locationId = locationId;
        }
        if (task.locationType === null && locationType !== null) {
          task.locationType = locationType;
        }
        return;
      }

      task.completedAt = completedAt;
      task.completionSource = completionSource;
      task.locationId = locationId;
      task.locationType = locationType;

      if (
        taskId === 'add_product' &&
        completionSource === 'wizard_product_create'
      ) {
        state.wizard.productCreatedDuringWizard = true;
      }

      updateLocationAllCompletedAt(state.tracker, locationId, completedAt, state.wizard.testLocationOptedIn);
    },

    /** Set collapsed state for a specific location. */
    setActivationTrackerCollapsed: (
      state,
      action: PayloadAction<{
        locationId: string;
        collapsed: boolean;
        /** Required when creating a new bucket; must be real. */
        locationType?: ActivationLocationType;
      }>,
    ) => {
      const { locationId, collapsed } = action.payload;
      if (!locationId) return;
      const existing = state.tracker.byLocation[locationId];
      if (!existing) {
        // Never invent buckets for unknown/test ids — only when caller proves real.
        if (action.payload.locationType !== 'real') return;
        state.tracker.byLocation[locationId] = createInitialLocationProgress();
      }
      state.tracker.byLocation[locationId].collapsed = collapsed === true;
    },

    /** Record celebration outcome for a specific location. */
    recordActivationCelebrationOutcome: (
      state,
      action: PayloadAction<{
        locationId: string;
        handledAt: string;
        outcome: ActivationConfettiOutcome;
      }>,
    ) => {
      const { locationId, handledAt, outcome } = action.payload;
      if (!locationId) return;
      if (!VALID_CONFETTI_OUTCOME.includes(outcome)) return;
      if (!allTasksCompleteForLocation(state.tracker, locationId, state.wizard.testLocationOptedIn)) return;
      const bucket = state.tracker.byLocation[locationId];
      if (!bucket) return;
      if (bucket.celebrationHandledAt !== null) return;
      bucket.celebrationHandledAt = handledAt;
      bucket.confettiOutcome = outcome;
    },

    /** Acknowledge completion for a specific location. */
    acknowledgeActivationCompletion: (
      state,
      action: PayloadAction<{ locationId: string; acknowledgedAt: string }>,
    ) => {
      const { locationId, acknowledgedAt } = action.payload;
      if (!locationId) return;
      if (!allTasksCompleteForLocation(state.tracker, locationId, state.wizard.testLocationOptedIn)) return;
      const bucket = state.tracker.byLocation[locationId];
      if (!bucket) return;
      if (bucket.completionAcknowledgedAt !== null) return;
      bucket.completionAcknowledgedAt = acknowledgedAt;
    },

    /** Set highlighted task for a specific location. */
    setActivationHighlightedTask: (
      state,
      action: PayloadAction<{
        locationId: string;
        taskId: ActivationTaskId | null;
        locationType?: ActivationLocationType;
      }>,
    ) => {
      const { locationId, taskId } = action.payload;
      if (!locationId) return;
      if (taskId !== null && !isKnownTaskId(taskId)) return;
      const existing = state.tracker.byLocation[locationId];
      if (!existing) {
        if (action.payload.locationType !== 'real') return;
        state.tracker.byLocation[locationId] = createInitialLocationProgress();
      }
      state.tracker.byLocation[locationId].lastHighlightedTaskId = taskId;
    },

    /** Replace state with a normalized recovered snapshot (rehydration repair). */
    replaceOwnerActivationState: (
      _state,
      action: PayloadAction<OwnerActivationState>,
    ) => migrateOwnerActivationState(action.payload),
  },
});

export const {
  markOwnerSetupEligible,
  markOwnerSetupSessionReady,
  markOwnerSetupSessionBootstrapError,
  resetOwnerActivationForIdentity,
  resetOwnerActivationState,
  startOwnerSetupWizard,
  viewOwnerSetupStep,
  completeOwnerSetupStep,
  goBackOwnerSetupStep,
  skipOwnerSetupWizard,
  completeOwnerSetupWizard,
  markWizardHomeLandingCelebrationShown,
  enterOwnerSetupReplay,
  exitOwnerSetupReplay,
  updateSimplifiedProductDraft,
  clearSimplifiedProductDraft,
  markWizardProductCreated,
  setTestLocationOptedIn,
  completeActivationTask,
  setActivationTrackerCollapsed,
  recordActivationCelebrationOutcome,
  acknowledgeActivationCompletion,
  setActivationHighlightedTask,
  replaceOwnerActivationState,
} = ownerActivationSlice.actions;

export default ownerActivationSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Compose from state.location without importing locationSlice (avoid circular dep).
 * Duck-type the location shape we need.
 */
type StateWithActivationAndLocation = {
  ownerActivation: OwnerActivationState;
  location?: {
    currentLocation?: {
      _id?: string;
      bp_location_is_system_generated?: boolean;
      bp_location_is_default?: boolean;
    } | null;
    isLocationSwitching?: boolean;
  };
};

type StateWithOwnerActivation = {
  ownerActivation: OwnerActivationState;
};

/** Read current location context from state (or return defaults). */
const getLocationContext = (
  state: StateWithActivationAndLocation,
): {
  locationId: string | null;
  isTest: boolean;
  isSwitching: boolean;
  isDefault: boolean;
} => {
  const loc = state.location?.currentLocation;
  return {
    locationId: loc?._id ?? null,
    isTest: loc?.bp_location_is_system_generated === true,
    isSwitching: state.location?.isLocationSwitching === true,
    isDefault: loc?.bp_location_is_default === true,
  };
};

export const selectOwnerActivation = (
  state: StateWithOwnerActivation,
): OwnerActivationState => state.ownerActivation;

export const selectOwnerSetupEligibility = (state: StateWithOwnerActivation) =>
  state.ownerActivation.eligibility;

export const selectOwnerSetupWizard = (state: StateWithOwnerActivation) =>
  state.ownerActivation.wizard;

export const selectOwnerSetupWizardStatus = (
  state: StateWithOwnerActivation,
): SetupWizardStatus => state.ownerActivation.wizard.status;

export const selectOwnerSetupCurrentStepId = (
  state: StateWithOwnerActivation,
): SetupWizardStepId | null => {
  const wizard = state.ownerActivation.wizard;
  if (wizard.status === 'in_progress') {
    return wizard.currentStepId ?? 'setup_progress';
  }
  return wizard.currentStepId;
};

export const selectShouldEnterInitialOwnerSetup = (
  state: StateWithOwnerActivation,
): boolean => {
  const { eligibility, wizard, ownerKey } = state.ownerActivation;
  if (!eligibility.eligible) return false;
  if (ownerKey === null) return false;
  if (eligibility.featureVersion !== OWNER_SETUP_FEATURE_VERSION) return false;
  return wizard.status === 'not_started' || wizard.status === 'in_progress';
};

export const selectOwnerSetupEntryMode = (
  state: StateWithOwnerActivation,
): SetupWizardEntryMode =>
  state.ownerActivation.wizard.status === 'not_started' ? 'initial' : 'resume';

export const selectCanSkipCurrentOwnerSetupStep = (
  state: StateWithOwnerActivation,
): boolean => {
  const wizard = state.ownerActivation.wizard;
  if (wizard.status !== 'in_progress') return false;
  return (wizard.currentStepId ?? 'setup_progress') !== 'setup_progress';
};

export const selectOwnerSetupProgress = (
  state: StateWithOwnerActivation,
): { currentIndex: number; totalSteps: number } => {
  const current = selectOwnerSetupCurrentStepId(state);
  return {
    currentIndex: current ? SETUP_WIZARD_STEPS.indexOf(current) : 0,
    totalSteps: SETUP_WIZARD_STEPS.length,
  };
};

export const selectIsOwnerSetupReplay = (
  state: StateWithOwnerActivation,
): boolean => state.ownerActivation.wizard.entryMode === 'replay';

/** Get merged tasks for current location. */
export const selectActivationTasks = (
  state: StateWithActivationAndLocation,
): Record<ActivationTaskId, ActivationTaskState> => {
  const { locationId } = getLocationContext(state);
  return getMergedActivationTasks(state.ownerActivation.tracker, locationId);
};

/** Get a specific task state for current location. */
export const selectActivationTaskById = (
  state: StateWithActivationAndLocation,
  taskId: ActivationTaskId,
): ActivationTaskState => {
  const tasks = selectActivationTasks(state);
  return tasks[taskId];
};

/** Count completed tasks for current location (only counts required tasks). */
export const selectActivationCompletedCount = (
  state: StateWithActivationAndLocation,
): number => {
  const tasks = selectActivationTasks(state);
  const requiredIds = getRequiredTaskIds(state.ownerActivation.wizard.testLocationOptedIn);
  return requiredIds.filter(
    taskId => tasks[taskId].completedAt !== null,
  ).length;
};

/** Check if all required tasks complete for current location. */
export const selectActivationAllComplete = (
  state: StateWithActivationAndLocation,
): boolean => {
  const tasks = selectActivationTasks(state);
  const requiredIds = getRequiredTaskIds(state.ownerActivation.wizard.testLocationOptedIn);
  return requiredIds.every(
    taskId => tasks[taskId].completedAt !== null,
  );
};

/** Get tracker phase for current location. */
export const selectActivationTrackerPhase = (
  state: StateWithActivationAndLocation,
): ActivationTrackerPhase => {
  const { wizard, tracker } = state.ownerActivation;
  const { locationId, isTest, isSwitching, isDefault } =
    getLocationContext(state);
  return getLocationActivationPhase({
    wizard,
    tracker,
    locationId,
    isTest,
    isSwitching,
    isDefault,
  });
};

/** Get collapsed state for current location. */
export const selectActivationTrackerCollapsed = (
  state: StateWithActivationAndLocation,
): boolean => {
  const { locationId } = getLocationContext(state);
  if (!locationId) return false;
  return (
    state.ownerActivation.tracker.byLocation[locationId]?.collapsed ?? false
  );
};

/** Check if completion acknowledged for current location. */
export const selectActivationCompletionAcknowledged = (
  state: StateWithActivationAndLocation,
): boolean => {
  const { locationId } = getLocationContext(state);
  if (!locationId) return false;
  return (
    state.ownerActivation.tracker.byLocation[locationId]
      ?.completionAcknowledgedAt !== null
  );
};

/** Check if confetti should play for current location. */
export const selectShouldPlayActivationConfetti = (
  state: StateWithActivationAndLocation,
): boolean => {
  const { locationId, isTest } = getLocationContext(state);
  if (!locationId || isTest) return false;
  const { tracker, wizard } = state.ownerActivation;
  if (!allTasksCompleteForLocation(tracker, locationId, wizard.testLocationOptedIn)) return false;
  const bucket = tracker.byLocation[locationId];
  if (!bucket) return false;
  return (
    bucket.celebrationHandledAt === null &&
    bucket.completionAcknowledgedAt === null
  );
};

/** Get highlighted task for current location. */
export const selectActivationHighlightedTask = (
  state: StateWithActivationAndLocation,
): ActivationTaskId | null => {
  const tasks = selectActivationTasks(state);
  const requiredIds = getRequiredTaskIds(state.ownerActivation.wizard.testLocationOptedIn);
  if (tasks.add_product.completedAt === null) return 'add_product';
  if (tasks.first_sale.completedAt === null) return 'first_sale';
  const remaining = [...ACTIVATION_TASKS]
    .filter(def => requiredIds.includes(def.id))
    .sort((a, b) => a.order - b.order)
    .find(definition => tasks[definition.id].completedAt === null);
  return remaining ? remaining.id : null;
};

/** Check if tracker should replace Quick Actions for current location. */
export const selectShouldReplaceOwnerQuickActions = (
  state: StateWithActivationAndLocation,
): boolean => {
  const phase = selectActivationTrackerPhase(state);
  return phase === 'active' || phase === 'completed_unacknowledged';
};

export const selectSimplifiedProductDraft = (
  state: StateWithOwnerActivation,
): SimplifiedProductDraft => state.ownerActivation.productDraft;

export const selectOwnerActivationOwnerKey = (
  state: StateWithOwnerActivation,
): string | null => state.ownerActivation.ownerKey;

export const matchesAuthenticatedOwner = (
  state: StateWithOwnerActivation,
  authenticatedOwnerId: string | null | undefined,
): boolean =>
  state.ownerActivation.ownerKey !== null &&
  !!authenticatedOwnerId &&
  state.ownerActivation.ownerKey === authenticatedOwnerId;

/** Whether the owner opted-in to the test location in the wizard. */
export const selectTestLocationOptedIn = (
  state: StateWithOwnerActivation,
): boolean => state.ownerActivation.wizard.testLocationOptedIn;

/** Get the raw tracker for direct access when needed. */
export const selectActivationTracker = (
  state: StateWithOwnerActivation,
): ActivationTrackerProgress => state.ownerActivation.tracker;

/**
 * True when the owner just exited the setup wizard (completed or skipped)
 * and the Home landing celebration modal has not yet been shown. Cleared
 * on first render via markWizardHomeLandingCelebrationShown.
 */
export const selectShouldPlayWizardHomeCelebration = (
  state: StateWithOwnerActivation,
): boolean => state.ownerActivation.wizard.homeLandingCelebrationPending;
